/**
 * فقاعة دعوة لتثبيت التطبيق — تظهر فوق زرّ «حسابي» في الناف بار السفليّ.
 * تظهر للموبايل فقط، ولمن لم يثبّت التطبيق، وتُخفى نهائياً عند الإغلاق أو التثبيت.
 */
import * as React from "react";
import { useNavigate } from "@tanstack/react-router";
import { Download, X } from "lucide-react";
import {
  getPwaInstallServerSnapshot,
  getPwaInstallSnapshot,
  promptPwaInstall,
  subscribePwaInstall,
} from "@/lib/pwaInstall";
import { trackPwaEvent } from "@/lib/pwaTrack";
import { useLocale } from "@/contexts/LocaleContext";

const DISMISS_KEY = "lecmo_pwa_nudge_dismissed";
const SHOW_DELAY_MS = 4000;

function wasDismissed(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return Boolean(window.localStorage.getItem(DISMISS_KEY));
  } catch {
    return false;
  }
}

export function PwaInstallNudge() {
  const navigate = useNavigate();
  const { locale, marketId, t } = useLocale();
  const market = marketId;
  const { prompt: deferred, installed, platform } = React.useSyncExternalStore(
    subscribePwaInstall,
    getPwaInstallSnapshot,
    getPwaInstallServerSnapshot,
  );
  const [ready, setReady] = React.useState(false);
  const [closed, setClosed] = React.useState(true);

  React.useEffect(() => {
    setClosed(wasDismissed());
    const timer = window.setTimeout(() => setReady(true), SHOW_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const eligible = !installed && (Boolean(deferred) || platform === "ios" || platform === "android");
  const visible = ready && !closed && eligible;

  React.useEffect(() => {
    if (!visible) return;
    void trackPwaEvent("card_viewed", { locale, market, meta: { surface: "bottom_nav_nudge" } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const dismiss = () => {
    setClosed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* تجاهل */
    }
  };

  if (!visible) return null;

  const handleClick = async () => {
    void trackPwaEvent("cta_clicked", {
      locale,
      market,
      meta: { surface: "bottom_nav_nudge", path: deferred ? "native_prompt" : "hint_page" },
    });
    if (deferred) {
      const outcome = await promptPwaInstall();
      if (outcome === "accepted") {
        void trackPwaEvent("prompt_accepted", { locale, market });
        dismiss();
        return;
      }
      if (outcome === "dismissed") {
        void trackPwaEvent("prompt_dismissed", { locale, market });
        dismiss();
        return;
      }
    }
    if (platform === "ios") {
      void trackPwaEvent("ios_hint_opened", { locale, market });
    }
    dismiss();
    void navigate({ to: "/account/install-app" });
  };

  return (
    <div className="pointer-events-none absolute bottom-full end-2 z-10 mb-2 max-w-[86vw] md:hidden">
      <div className="pointer-events-auto relative flex items-center gap-2 border border-foreground/15 bg-background px-3 py-2 shadow-lg">
        <button
          type="button"
          onClick={handleClick}
          className="flex min-h-[36px] items-center gap-2 text-start"
        >
          <Download className="h-4 w-4 shrink-0 text-primary" strokeWidth={1.5} />
          <span className="text-[12.5px] leading-snug text-foreground">
            {t("pwa.nudge_title")}
          </span>
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("pwa.nudge_dismiss")}
          className="flex h-8 w-8 shrink-0 items-center justify-center text-foreground/50"
        >
          <X className="h-3.5 w-3.5" strokeWidth={1.5} />
        </button>
        <span
          aria-hidden="true"
          className="absolute -bottom-[5px] end-6 h-2 w-2 rotate-45 border-b border-e border-foreground/15 bg-background"
        />
      </div>
    </div>
  );
}
